import { ChangeEvent, useRef, useState } from "react";
import styled from "@emotion/styled";
import { ValidationErrorStyle } from "src/common";
import { Flex, InputLabel, Text, CloseIcon } from "src/components";
import { colors } from "src/theme";

const Box = styled.div({
  display: "flex",
  flexDirection: "column",
  gap: "8px",
});

const InputContainer = styled.div({
  position: "relative",
  width: "100%",
  minHeight: "42px",
  border: `1px solid ${colors.border5}`,
  borderRadius: "8px",
  padding: "9px 12px",
  display: "flex",
  flexWrap: "wrap",
  alignItems: "center",
  gap: "4px",
  backgroundColor: colors.background7,
});

const StyledInput = styled.input({
  border: "none",
  outline: "none",
  padding: "2px",
  fontSize: "16px",
  flexGrow: 1,
  minWidth: "20px",
  backgroundColor: "transparent",
});

const OptionsList = styled.div({
  width: "100%",
  boxSizing: "border-box" as const,
  outline: "none",
  display: "block",
  padding: "9px 12px",
  fontSize: "18px",
  fontWeight: 400,
  fontStyle: "normal",
  lineHeight: "24px",
  color: colors.text1,
  borderRadius: "8px",
  border: `1px solid ${colors.border5}`,
  transition: "border-color .15s ease-in-out, box-shadow .15s ease-in-out",
  backgroundColor: colors.background7,
  maxHeight: "150px",
  overflowY: "auto" as const,
  zIndex: 10,
});

const Tag = styled.span({
  backgroundColor: colors.primary5,
  padding: "2px 8px",
  borderRadius: "8px",
  cursor: "pointer"
});

const OptionItem = styled.div({
  cursor: "pointer",
  padding: "4px 0",

  "&:hover": {
    backgroundColor: colors.background6,
  },
});

interface SelectOption {
  value: string;
  label: string;
}

interface MultiSelectProps {
  id?: string;
  label?: string;
  value?: string[];
  error?: string;
  disabled?: boolean;
  options: SelectOption[];
  onChange?: (value: string[]) => void;
}

export const MultiSelect: React.FC<MultiSelectProps> = ({
  id,
  label,
  value = [],
  error,
  disabled,
  options,
  onChange,
}) => {
  const [inputValue, setInputValue] = useState("");
  const [showOptions, setShowOptions] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const filteredOptions = options.filter(
    (opt) =>
      !value.includes(opt.value) &&
      opt.label.toLowerCase().includes(inputValue.toLowerCase())
  );

  const handleContainerClick = () => {
    inputRef.current?.focus();
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
    setShowOptions(true);
  };

  const handleOptionClick = (option: SelectOption) => {
    const newSelectedValues = [...value, option.value];
    setInputValue("");
    inputRef.current?.focus();
    if (onChange) {
      onChange(newSelectedValues);
    }
  };

  const handleBlur = (e: React.FocusEvent) => {
    // Provjeri je li novi fokusirani element unutar containera
    if (!containerRef.current?.contains(e.relatedTarget as Node)) {
      setShowOptions(false);
    }
  };

  const handleRemoveValue = (valueToRemove: string) => {
    const newSelectedValues = value.filter((v) => v !== valueToRemove);
    if (showOptions) inputRef.current?.focus();
    if (onChange) {
      onChange(newSelectedValues);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace" && inputValue === "" && value.length > 0) {
      handleRemoveValue(value[value.length - 1]);
    }
  };

  return (
    <Box ref={containerRef} onBlur={handleBlur}>
      {label ? (
        <InputLabel htmlFor={id} disabled={disabled}>
          {label}
        </InputLabel>
      ) : null}
      <InputContainer onClick={handleContainerClick}>
        {value.map((val) => {
          const option = options.find((opt) => opt.value === val);
          return option ? (
            <Tag key={val}>
              <Flex align="center" gap="4px">
                <Text type="small" color={colors.primary1}>
                  {option.label}
                </Text>

                <CloseIcon
                  color={colors.primary1}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemoveValue(val);
                  }}
                />
              </Flex>
            </Tag>
          ) : null;
        })}
        <StyledInput
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onFocus={() => setShowOptions(true)}
          disabled={disabled}
          autoComplete="off"
        />
      </InputContainer>
      {error ? <ValidationErrorStyle>{error}</ValidationErrorStyle> : null}
      {showOptions && filteredOptions.length > 0 && (
        <OptionsList>
          {filteredOptions.map((option) => (
            <OptionItem
              key={option.value}
              onClick={() => handleOptionClick(option)}
              tabIndex={0}
            >
              {option.label}
            </OptionItem>
          ))}
        </OptionsList>
      )}
    </Box>
  );
};
